import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { CurrencyInfo, StoreLanguage } from '@core/domain/models/store.models';
import { Resolutions } from '@core/domain/state/core.state';
import { getCountryName, getCurrencyInfo, getCurrentLanguage } from '@core/infrastructure/state/core.selectors';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import { ChangeLanguageAnalytics, OpenWwAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { MarketService } from '@shared/market/services/market.service';
import {
    getMarketLanguages,
    InternalNavigation,
    MarketLanguage,
} from '@shared/market/store';
import { GoTo } from '@shared/navigation/store/navigation.actions';

@Component({
    selector: 'piece-select-store-language',
    templateUrl: './piece-select-store-language.component.html',
    styleUrls: ['./piece-select-store-language.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PieceSelectStoreLanguageComponent { // Selector de idioma contemplando settings del CMS
    @Input() public element: ContentItemTree;

    @Input() public direction: string;

    @Input() public resolution: Resolutions;

    public countryName$: Observable<string> = this.store.select(getCountryName);

    public selectedLanguage$: Observable<StoreLanguage> = this.store.select(getCurrentLanguage);

    public openWWButtonText$: Observable<string> = this.countryName$;

    public marketLanguage$: Observable<MarketLanguage> = this.store.select(getMarketLanguages).pipe(
        filter((languages: MarketLanguage[]) => languages?.length > 0),
        concatLatestFrom(() => this.selectedLanguage$),
        map(([languages, selectedLanguage]) => {
            return languages.find(lang => lang.id === selectedLanguage.id);
        }),
    );

    public currency$: Observable<CurrencyInfo> = this.store.select(getCurrencyInfo);

    constructor(
        protected readonly store: Store,
        protected readonly marketService: MarketService,
        private readonly router: Router,
        @Inject(WINDOW) private readonly window: CommonWindow
    ) {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.store.dispatch(InternalNavigation());
            }
        });
    }

    public canChangeCountry$: Observable<boolean> = this.marketService.canChangeCountry$.pipe(
        map((canChange: boolean) => {
            return canChange && !!this.element?.settings?.general?.allowCountryChange;
        })
    );

    public navigateToNewLanguage(language: MarketLanguage): void {
        if (!!this.element?.settings?.general?.noNavigation) {
            this.store.dispatch(GoTo({ url: language.href + this.window.location.search, reload: true }));
        } else {
            this.store.dispatch(GoTo({ url: language.baseUrl + this.window.location.search, reload: true }));
        }
    }

    public openWW($event: MouseEvent): void {
        $event.preventDefault(); // ECOMDUTI-22526

        this.store.dispatch(OpenWwAnalytics());
        this.canChangeCountry$.pipe(first()).subscribe((canChange: boolean) => {
            if (canChange) this.marketService.gotToWW();
        });
    }

    public changeLanguageTo($event: MouseEvent, language: MarketLanguage): void {
        // El cambio de idioma siempre debe ser por https:// y no por SPA
        $event.preventDefault();
        $event.stopPropagation();

        this.store.dispatch(ChangeLanguageAnalytics({ langName: language.name }));
        this.navigateToNewLanguage(language);
    }
}
